import { useEffect, useState } from "react";
import { Box, Flex, Text, Badge, useToast } from "@chakra-ui/react";
import { ButtonCustom } from "../../../components/form/controls/form.button";
import { jobService } from "../../../service/job/job.service";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import inventoryService from "../../../service/inventory/inventory.service";
import { downloadCsvToMachine } from "../../../util/file.util";
import { InventoryTable } from "../../../components/app/inventory/inventory.table";
import { UploadInventoryModal } from "../../../components/app/inventory/upload-inventory.modal";
import { AddInventoryModal } from "../../../components/app/inventory/add-inventory.modal";
import { InventoryModel } from "../../../models/inventory.model";
import { InventoryBulkFilter } from "../../../models/filter/inventory-bulk.filter";
import { JobDetailHeader } from "../../../components/app/job/job-detail.header";
import { useAppDispatch, useAppSelector } from "../../../util/hooks.util";
import { updateJobFilter as inventoryUpdate } from "../../../store/slice/Account/inventory.slice";
import { DownloadIcon } from "@chakra-ui/icons";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { TableFooter } from "../../../components/shared/table/TableFooter";
import { JobStatus } from "../../../models/enum/job-status.enum";
import { JobDetailContent } from "../../../components/app/job/job-detail-content.header";
import { Loading } from "../../../components/shared/Loading";
import { openaiService } from "../../../service/integrations/openai-service.service";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useMemo } from "react";
import { errorToast, successToast } from "../../../constants/toast.constants";
import { Modal } from "../../../components/app/Modal";
import { convertIsoTime } from "../../../util/date.util";
import { FaDollarSign } from "react-icons/fa";
import { EditInternalJobModal } from "../../../components/app/job/edit-internal-job.modal";

const DEFAULT_PAGE_SIZE = 48;
const DEFAULT_PAGE_SIZE_OPTIONS = { 24: 24, 48: 48, 96: 96 };

export const InternalJobDetail = ({}) => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const { region } = useSelector((state: RootState) => state.userProfile);
  const { jobFilter: inventoryFilter, jobSortColumn: inventorySort } =
    useAppSelector((state) => state.inventory);

  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [uploadSheetState, setUploadSheetState] = useState({
    open: false,
    isEdit: false,
  });

  const [job, setJob] = useState({} as any);
  const [inventory, setInventory] = useState({
    data: [],
    totalItems: 0,
    totalPage: 0,
  } as { data: InventoryModel[]; totalPage: number; totalItems: number });

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [checkedIds, setCheckedIds] = useState([] as string[]);
  const [refreshKey, setRefreshKey] = useState(0);

  const [activeInventory, setActiveInventory] = useState(null);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [searchParamReady, setSearchParamReady] = useState(false);
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);
  const [quoteSentModalWarningOpen, setQuoteSentModalWarningOpen] =
    useState(false);

  const canSendQuote = useMemo(
    () => inventory.data.every((i) => !!i.quotePrice),
    [inventory.data]
  );

  useEffect(() => {
    if (searchParams.get("inventory_id")) {
      setActiveInventory(searchParams.get("inventory_id"));
    }
    if (searchParams.get("searchTerm")) {
      setGlobalSearchTerm(searchParams.get("searchTerm"));
    }

    setSearchParamReady(true);
  }, [searchParams]);

  const fetchJob = async () => {
    const prms = new Promise((resolve, reject) =>
      jobService.get(id).then((job) => {
        setJob(job);
        setQuoteSent(!!job.quoteSentDate);
        resolve(job);
      })
    );

    return await prms;
  };

  const fetchInventory = async (page?: number, sort?: any) => {
    const filterPayload = inventorySort
      ? { ...inventorySort, searchTerm: globalSearchTerm }
      : { searchTerm: globalSearchTerm };

    await inventoryService
      .getList(
        {
          jobId: id,
          ...filterPayload,
          ids: activeInventory ? [activeInventory] : undefined,
        },
        undefined,
        page,
        pageSize,
        sort ?? inventorySort,
        region
      )
      .then((inventory) => {
        setInventory(inventory);
        setRefreshKey((prev) => prev + 1);
      });
  };

  const handleExport = async () => {
    const obj = {
      jobId: id,
      ids: !!checkedIds?.length ? checkedIds : undefined,
      ...inventoryFilter,
    };

    setLoading(true);
    await inventoryService
      .exportToCsv(obj)
      .then((csvData) => {
        downloadCsvToMachine(csvData, "inventory-export.csv");
      })
      .finally(() => setLoading(false));
  };

  const bulkUpdateInventory = async () => {
    await openaiService.getInventoryWeight(id);
  };

  const handleSendQuote = async () => {
    if (!job.contactUserId) {
      toast(errorToast("Please add a contact user to this job first."));
      return;
    }

    setQuoteSending(true);
    await jobService
      .sendQuote(id)
      .then(() => {
        toast(successToast("Quote sent successfully."));
        setQuoteSent(true);
      })
      .catch(() => {
        toast(errorToast("Failed to send quote."));
      }).finally(() => {
        setQuoteSentModalWarningOpen(false);
        setQuoteSending(false);
      });
  };

  useEffect(() => {
    bulkUpdateInventory();
    fetchJob();
  }, []);

  useEffect(() => {
    if (searchParamReady) {
      fetchInventory(pageNum, inventorySort);
    }
  }, [searchParamReady, inventoryFilter, pageNum, inventorySort]);

  useEffect(() => {
    fetchInventory(1, inventorySort);
  }, [pageSize]);

  return (
    <>
      <Loading loading={loading} />
      <Flex flexDirection="column">
        <Box
          bg="white"
          mb="24px"
          boxShadow="base"
          px={4}
          py={2}
          borderRadius="md"
          width="100%"
        >
          <Flex justifyContent="space-between">
            <Flex flexDir="row" gap="8px">
              <Box fontSize="26px">{job.region === "PH" ? "🇵🇭" : "🇦🇺"}</Box>
              <Flex align="center">
                <Badge
                  colorScheme={
                    job.status === "COMPLETE"
                      ? "green"
                      : job.status === "PENDING" || job.status === "COLLECTION"
                      ? "orange"
                      : "red"
                  }
                >
                  {JobStatus[job.status]}
                </Badge>
              </Flex>
              <Flex hidden={!job.rebateRequired} align="center">
                <Badge colorScheme={"gray"}>
                  {job.rebateRequired ? "Rebate required" : ""}
                </Badge>
              </Flex>
              <Flex hidden={!job.showWasteImpact} align="center">
                <Badge colorScheme={"blue"}>
                  {job.showWasteImpact ? "Social Impact" : ""}
                </Badge>
              </Flex>
            </Flex>

            <Flex flexDir="row" gap="8px">
              <JobDetailHeader
                id={id}
                inventoryCount={inventory?.totalItems}
                companyName={job.companyName}
              />

              <ButtonCustom neutral onClick={() => setEditModalOpen(true)}>
                Edit Internal Job
              </ButtonCustom>

              <ButtonCustom
                disabled={!canSendQuote || quoteSending}
                loading={quoteSending}
                onClick={() => {
                  if (quoteSent) {
                    setQuoteSentModalWarningOpen(true);
                  } else {
                    handleSendQuote();
                  }
                }}
              >
                <FaDollarSign />
                <Box ml="4px">{quoteSent ? "Quote Sent" : "Send Quote"}</Box>
              </ButtonCustom>
            </Flex>
          </Flex>

          <Flex justifyContent="space-between">
            <Flex
              flexDir="row"
              gap="6px"
              fontSize="3xl"
              mb={2}
              fontWeight="bold"
            >
              <Text>INTERNAL-{job.jobNo}</Text>
            </Flex>

            {/* <ButtonCustom
                style={{ marginTop: "10px" }}
                danger
                disabled={quoteSending || job.status !== "COMPLETE"}
                loading={quoteSending}
                onClick={() => {
                  jobService.convertToInventory(id).then(() => {
                    toast(successToast("Job converted to standard job successfully."));
                    navigate(`/app/job/${id}`);
                  }).catch(() => {
                    toast(errorToast("Failed to convert job to standard job."));
                  });
                }}
              >
                <Box ml="4px">Convert to Standard Job</Box>
              </ButtonCustom> */}
          </Flex>

          {/* card contents */}
          <JobDetailContent
            job={job}
            inventory={inventory.data}
            onUpload={async () => {
              await fetchJob();
              await fetchInventory();
            }}
          />
        </Box>
        <Flex flexDirection="row" gap="6px" justifyContent="flex-end">
          <Flex flexDir="row" gap="6px">
            {/* <ButtonCustom onClick={() => setModalOpen(true)}>
              <AddIcon />
              New Item
            </ButtonCustom> */}
            <ButtonCustom
              disabled={!inventory?.data?.length}
              secondary
              onClick={handleExport}
            >
              Export to csv
              <DownloadIcon ml="6px" />
            </ButtonCustom>
            {/* <ButtonCustom
              neutral
              secondary
              onClick={() => setUploadSheetState({ open: true, isEdit: false })}
            >
              Upload New
              <UploadFileIcon />
            </ButtonCustom> */}
            <ButtonCustom
              neutral
              secondary
              onClick={() => setUploadSheetState({ open: true, isEdit: true })}
            >
              Update Existing
              <UploadFileIcon />
            </ButtonCustom>
          </Flex>
        </Flex>
      </Flex>
      <InventoryTable
        key={refreshKey}
        excludeColumns={["status", "salePrice", "rebate", "statusUpdatedDate"]}
        inventory={inventory.data}
        isJobDetail={true}
        onDelete={(id: string) =>
          setInventory({
            data: inventory.data.filter((i) => i.id !== id),
            totalPage: inventory.totalPage,
            totalItems: inventory.totalItems,
          })
        }
        onBulkDelete={async () => await fetchInventory(pageNum)}
        onCheckChange={(data) =>
          setCheckedIds(data.filter((x) => x.isChecked).map((x) => x.id))
        }
        onEdit={async () => await fetchInventory(pageNum)}
        onFilterChange={async (filter: InventoryBulkFilter) => {
          setPageNum(1);
          dispatch(inventoryUpdate(filter));
          await fetchInventory(1);
        }}
        onSort={() => setPageNum(1)}
        footer={
          <TableFooter
            count={inventory.data?.length}
            totalItems={inventory.totalItems}
            totalPage={inventory.totalPage}
            pageNum={pageNum}
            pageSize={pageSize}
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            onChange={async (num) => {
              setPageNum(num);
              await fetchInventory(num);
            }}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNum(1);

              // Useeffect will trigger
            }}
          />
        }
      />
      <AddInventoryModal
        jobId={id}
        assignedAdminId={job.assignedAdminId}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={fetchInventory}
      />

      <EditInternalJobModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        job={job}
        onSubmit={fetchJob}
      />

      <UploadInventoryModal
        jobId={id}
        isBulkEdit={uploadSheetState.isEdit}
        isOpen={uploadSheetState.open}
        onClose={() => setUploadSheetState({ open: false, isEdit: false })}
        onSubmit={fetchInventory}
      />

      <Modal
        header="! Quote Already Sent"
        isOpen={quoteSentModalWarningOpen}
        onClose={() => setQuoteSentModalWarningOpen(false)}
      >
        <Text>
          This job has already sent a quote on:{" "}
          {convertIsoTime(job.quoteSentDate)}
          <br />
          <br />
          <Flex justifyContent="flex-end" gap="6px">
            <ButtonCustom onClick={() => setQuoteSentModalWarningOpen(false)}>
              Cancel
            </ButtonCustom>
            <ButtonCustom neutral onClick={handleSendQuote} loading={quoteSending}>
              Send Quote Again
            </ButtonCustom>
          </Flex>
        </Text>
      </Modal>
    </>
  );
};
